import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import {
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock3,
  Filter,
  Music2,
  PencilLine,
  Printer,
  RefreshCw,
  Search,
  Trash2,
  Upload,
  Volume2,
  VolumeX,
} from "lucide-react";
import {
  Button,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  Skeleton,
  StatusBadge,
} from "../components/ui";
import { useOrderAlert } from "../context/OrderAlertContext";
import { apiRequest, jsonBody } from "../lib/api";
import { blobToBase64 } from "../lib/images";
import {
  formatDateTime,
  formatDateTimeWithZone,
  formatRupiah,
  statusLabel,
} from "../lib/format";
import {
  normalizeCollection,
  type ApiRecord,
  type SessionUser,
} from "../lib/types";
import { formatOrderStageDuration } from "../../shared/order-alert";
import { calculateOrderAmendment } from "../../shared/order-amendment";
import { MEMBER_POINT_VALUE_RUPIAH } from "../../shared/loyalty";
import {
  needsPaymentMark,
  UNMARKED_PAYMENT_FILTER,
} from "../../shared/order-payment-filter";

const get = (item: ApiRecord, ...keys: string[]) =>
  keys.map((key) => item[key]).find((entry) => entry !== undefined && entry !== null);
const asRecord = (input: unknown): ApiRecord =>
  input && typeof input === "object" && !Array.isArray(input)
    ? (input as ApiRecord)
    : {};
const asText = (input: unknown, fallback = "") =>
  typeof input === "string" || typeof input === "number"
    ? String(input)
    : fallback;
const asNumber = (input: unknown, fallback = 0) =>
  Number.isFinite(Number(input)) ? Number(input) : fallback;
const asBoolean = (input: unknown) =>
  input === true || input === 1 || input === "1";
const orderTimeZone = (item: ApiRecord) =>
  asText(
    get(
      item,
      "serviceTimeZone",
      "service_timezone",
      "resolvedTimeZone",
      "resolved_time_zone",
    ),
    "Asia/Makassar",
  );

const terminalStatuses = new Set(["COMPLETED", "CANCELLED", "REJECTED"]);
const unconfirmedStatuses = new Set(["NEW", "WAITING_CONFIRMATION"]);
const ORDERS_PAGE_SIZE = 30;

function orderColorIndex(value: string): number {
  let hash = 0;
  for (const character of value) hash = (hash * 31 + character.charCodeAt(0)) | 0;
  return Math.abs(hash) % 6;
}

function nextOrderStatus(status: string, deliveryType: string): string | undefined {
  if (unconfirmedStatuses.has(status)) return "CONFIRMED";
  if (status === "CONFIRMED" || status === "PAID") return "PREPARING";
  if (status === "PREPARING") {
    return deliveryType === "PICKUP"
      ? "READY_FOR_PICKUP"
      : "READY_FOR_DELIVERY";
  }
  if (status === "READY_FOR_DELIVERY") return "DELIVERING";
  if (status === "READY_FOR_PICKUP") return "COMPLETED";
  return undefined;
}

type OrderCardSize = "compact" | "comfortable" | "large";

function readStoredOrderCardSize(): OrderCardSize {
  if (typeof window === "undefined") return "compact";
  const value = window.localStorage.getItem("sedapsayur_order_card_size");
  return value === "comfortable" || value === "large" ? value : "compact";
}

interface OrderDetail {
  order: ApiRecord;
  items: ApiRecord[];
  events: ApiRecord[];
  amendments: ApiRecord[];
}

interface OrderItemPreview {
  orderItemId: string;
  productId: string;
  productName: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

function parseOrderItemPreview(input: unknown): OrderItemPreview[] {
  let parsed = input;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((entry) => {
      const item = asRecord(entry);
      return {
        orderItemId: asText(get(item, "orderItemId", "order_item_id", "id")),
        productId: asText(get(item, "productId", "product_id")),
        productName: asText(
          get(item, "productName", "product_name", "product_name_snapshot"),
        ),
        unit: asText(get(item, "unit", "productUnit", "product_unit_snapshot")),
        quantity: asNumber(item.quantity),
        unitPrice: asNumber(get(item, "unitPrice", "unit_price")),
        subtotal: asNumber(item.subtotal),
      };
    })
    .filter((item) => item.productName && item.quantity > 0);
}

interface OrderEditTarget {
  card: ApiRecord;
  order: ApiRecord;
  items: ApiRecord[];
}

function mergeAmendmentSnapshot(row: ApiRecord, snapshot: ApiRecord): ApiRecord {
  return {
    ...row,
    subtotal: asNumber(snapshot.subtotal, asNumber(row.subtotal)),
    deliveryFee: asNumber(
      get(snapshot, "deliveryFee", "delivery_fee"),
      asNumber(get(row, "deliveryFee", "delivery_fee")),
    ),
    delivery_fee: asNumber(
      get(snapshot, "deliveryFee", "delivery_fee"),
      asNumber(get(row, "deliveryFee", "delivery_fee")),
    ),
    discount: asNumber(snapshot.discount, asNumber(row.discount)),
    total: asNumber(snapshot.total, asNumber(row.total)),
    contentVersion: asNumber(
      get(snapshot, "contentVersion", "content_version"),
      asNumber(get(row, "contentVersion", "content_version"), 1),
    ),
    content_version: asNumber(
      get(snapshot, "contentVersion", "content_version"),
      asNumber(get(row, "contentVersion", "content_version"), 1),
    ),
    itemCount: asNumber(get(snapshot, "itemCount", "item_count")),
    item_count: asNumber(get(snapshot, "itemCount", "item_count")),
    itemSummary: asText(get(snapshot, "itemSummary", "item_summary")),
    item_summary: asText(get(snapshot, "itemSummary", "item_summary")),
    itemPreviewJson: get(snapshot, "itemPreviewJson", "item_preview_json"),
    item_preview_json: get(snapshot, "itemPreviewJson", "item_preview_json"),
    updatedAt: asText(get(snapshot, "updatedAt", "updated_at")),
    updated_at: asText(get(snapshot, "updatedAt", "updated_at")),
  };
}

export function OrderManagementPage({
  role,
  paymentsOnly = false,
}: {
  role: SessionUser["role"];
  paymentsOnly?: boolean;
}) {
  const endpoint = role === "OWNER" ? "/api/owner/orders" : "/api/admin/orders";
  const [items, setItems] = useState<ApiRecord[]>([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [unpaidCount, setUnpaidCount] = useState<number>();
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersHasMore, setOrdersHasMore] = useState(false);
  const summaryRequest = useRef(0);
  const listRequest = useRef(0);
  const paymentRefreshTimer = useRef<number | undefined>(undefined);
  const unmarkedPayment = !paymentsOnly && status === UNMARKED_PAYMENT_FILTER;
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busyId, setBusyId] = useState<string>();
  const [archiveTarget, setArchiveTarget] = useState<ApiRecord>();
  const [receiptTarget, setReceiptTarget] = useState<ApiRecord>();
  const [receiptPrintedAt, setReceiptPrintedAt] = useState("");
  const [editTarget, setEditTarget] = useState<OrderEditTarget>();
  const [editQuantities, setEditQuantities] = useState<Record<string, string>>({});
  const [editReason, setEditReason] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [expandedId, setExpandedId] = useState<string>();
  const [detailBusyId, setDetailBusyId] = useState<string>();
  const [details, setDetails] = useState<Record<string, OrderDetail>>({});
  const [alertBusyBranch, setAlertBusyBranch] = useState<string>();
  const [cardSize, setCardSize] = useState<OrderCardSize>(
    readStoredOrderCardSize,
  );
  const {
    alertSettings,
    refreshAlertSettings,
    isLive,
    muted,
    setMuted,
    volume,
    setVolume,
    soundBlocked,
    activateSound,
    lastEvent,
  } = useOrderAlert();

  const load = useCallback(async (background = false) => {
    const request = ++listRequest.current;
    const countRequest = ++summaryRequest.current;
    const includePaymentSummary = !paymentsOnly && ordersPage === 1;
    if (!background) {
      setLoading(true);
      setError(undefined);
      if (includePaymentSummary) setUnpaidCount(undefined);
    }
    try {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      if (unmarkedPayment) {
        params.set("unmarked_payment", "1");
      } else if (status) {
        params.set(paymentsOnly ? "payment_status" : "status", status);
      }
      params.set("page", String(ordersPage));
      if (includePaymentSummary) params.set("payment_summary", "1");
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      params.set("limit", String(ORDERS_PAGE_SIZE));
      const payload = await apiRequest<unknown>(
        `${endpoint}?${params.toString()}`,
      );
      if (request !== listRequest.current) return;
      const nextItems = normalizeCollection(payload).items;
      const summary = asRecord(payload);
      if (includePaymentSummary && countRequest === summaryRequest.current) {
        setUnpaidCount(typeof summary.unpaidCount === "number" ? summary.unpaidCount : undefined);
      }
      setOrdersHasMore(summary.hasMore === true);
      setItems(nextItems);
      setReceiptTarget((current) => {
        if (!current) return current;
        return (
          nextItems.find((row) => asText(row.id) === asText(current.id)) ??
          current
        );
      });
    } catch (caught) {
      if (request !== listRequest.current) return;
      const message =
        caught instanceof Error ? caught.message : "Pesanan belum dapat dimuat.";
      if (background) setNotice(message);
      else setError(message);
    } finally {
      if (request === listRequest.current) setLoading(false);
    }
  }, [dateFrom, dateTo, endpoint, ordersPage, paymentsOnly, query, status, unmarkedPayment]);

  const refreshPaymentCount = useCallback(() => {
    if (paymentsOnly) return;
    window.clearTimeout(paymentRefreshTimer.current);
    paymentRefreshTimer.current = window.setTimeout(() => {
      const request = ++summaryRequest.current;
      const params = new URLSearchParams({ payment_summary: "only" });
      if (query) params.set("q", query);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      void apiRequest<{ unpaidCount: number }>(`${endpoint}?${params.toString()}`)
        .then((result) => {
          if (request === summaryRequest.current) {
            setUnpaidCount(typeof result.unpaidCount === "number" ? result.unpaidCount : undefined);
          }
        })
        .catch(() => {
          if (request === summaryRequest.current) setUnpaidCount(undefined);
        });
    }, 300);
  }, [dateFrom, dateTo, endpoint, paymentsOnly, query]);

  useEffect(() => () => {
    window.clearTimeout(paymentRefreshTimer.current);
    summaryRequest.current += 1;
  }, [refreshPaymentCount]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), query ? 250 : 0);
    return () => {
      window.clearTimeout(timer);
      listRequest.current += 1;
    };
  }, [load, query]);
  useEffect(() => {
    window.localStorage.setItem("sedapsayur_order_card_size", cardSize);
  }, [cardSize]);

  useEffect(() => {
    const payload = lastEvent?.event;
    if (!payload) return;
    const timer = window.setTimeout(() => {
      if (payload.type === "ORDER_CREATED") {
        setNotice(
          `Pesanan baru ${payload.orderNumber ?? ""} masuk. Daftar telah diperbarui.`,
        );
        if (ordersPage === 1) void load(true);
        else setOrdersPage(1);
        return;
      }
      if (payload.type === "ORDER_UPDATED" && payload.orderId) {
        if (payload.contentChanged) {
          setDetails((current) => {
            const next = { ...current };
            delete next[payload.orderId ?? ""];
            return next;
          });
          setNotice("Isi pesanan diperbarui. Total, stok, dan resi sudah disesuaikan.");
          void load(true);
          return;
        }
        refreshPaymentCount();
        setItems((current) =>
          current.map((row) =>
            asText(row.id) === payload.orderId
              ? {
                  ...row,
                  ...(payload.orderStatus
                    ? {
                        orderStatus: payload.orderStatus,
                        order_status: payload.orderStatus,
                        currentStatusStartedAt: payload.updatedAt,
                        current_status_started_at: payload.updatedAt,
                      }
                    : {}),
                  ...(payload.paymentStatus
                    ? {
                        paymentStatus: payload.paymentStatus,
                        payment_status: payload.paymentStatus,
                      }
                    : {}),
                  ...(payload.updatedAt
                    ? {
                        updatedAt: payload.updatedAt,
                        updated_at: payload.updatedAt,
                      }
                    : {}),
                }
              : row,
          ),
        );
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [lastEvent, load, ordersPage, refreshPaymentCount]);

  const update = async (
    item: ApiRecord,
    nextStatus: string,
    payment = false,
  ) => {
    const id = asText(item.id);
    setBusyId(id);
    try {
      const result = asRecord(await apiRequest<unknown>(
        `${endpoint}/${encodeURIComponent(id)}/${payment ? "payment" : "status"}`,
        {
          method: "PATCH",
          ...jsonBody(
            payment
              ? { status: nextStatus }
              : {
                  status: nextStatus,
                  ...(terminalStatuses.has(nextStatus)
                    ? { reason: "Diperbarui melalui dashboard" }
                    : {}),
                },
          ),
        },
      ));
      const updatedAt = asText(get(result, "updatedAt", "updated_at"), new Date().toISOString());
      setItems((current) =>
        current.map((row) =>
          asText(row.id) === id
            ? {
                ...row,
                ...(payment
                  ? { paymentStatus: nextStatus, payment_status: nextStatus }
                  : {
                      orderStatus: nextStatus,
                      order_status: nextStatus,
                      currentStatusStartedAt: updatedAt,
                      current_status_started_at: updatedAt,
                    }),
                updatedAt,
                updated_at: updatedAt,
              }
            : row,
        ),
      );
      setNotice("Status berhasil diperbarui. Kartu tetap pada posisi semula.");
      refreshPaymentCount();
      await refreshAlertSettings();
      if (expandedId === id) await fetchDetail(id, true);
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Status belum berhasil diperbarui.",
      );
    } finally {
      setBusyId(undefined);
    }
  };

  const fetchDetail = async (id: string, force = false) => {
    if (!force && details[id]) {
      setExpandedId((current) => (current === id ? undefined : id));
      return;
    }
    setDetailBusyId(id);
    try {
      const payload = asRecord(
        await apiRequest<unknown>(`${endpoint}/${encodeURIComponent(id)}`),
      );
      const detail: OrderDetail = {
        order: asRecord(payload.order),
        items: Array.isArray(payload.items) ? payload.items.map(asRecord) : [],
        events: Array.isArray(payload.events) ? payload.events.map(asRecord) : [],
        amendments: Array.isArray(payload.amendments)
          ? payload.amendments.map(asRecord)
          : [],
      };
      setDetails((current) => ({ ...current, [id]: detail }));
      setExpandedId(id);
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Rincian pesanan belum dapat dimuat.",
      );
    } finally {
      setDetailBusyId(undefined);
    }
  };

  const archive = async () => {
    if (!archiveTarget) return;
    const id = asText(archiveTarget.id);
    setBusyId(id);
    try {
      await apiRequest(`/api/admin/orders/${encodeURIComponent(id)}`, {
        method: "DELETE",
        ...jsonBody({ reason: "Diarsipkan manual oleh Admin" }),
      });
      setArchiveTarget(undefined);
      setNotice(
        "Pesanan dihapus dari Admin dan tetap terlihat Owner selama 30 hari.",
      );
      await load();
    } catch (caught) {
      setNotice(
        caught instanceof Error ? caught.message : "Pesanan belum dapat diarsipkan.",
      );
    } finally {
      setBusyId(undefined);
    }
  };

  const saveAlertSetting = async (
    setting: ApiRecord,
    changes: { isEnabled?: boolean; playbackMode?: string },
  ) => {
    const branchId = asText(get(setting, "branchId", "branch_id"));
    setAlertBusyBranch(branchId);
    try {
      await apiRequest(`/api/order-alerts/${encodeURIComponent(branchId)}`, {
        method: "PUT",
        ...jsonBody({
          isEnabled:
            changes.isEnabled ?? asBoolean(get(setting, "isEnabled", "is_enabled")),
          playbackMode:
            changes.playbackMode ??
            asText(get(setting, "playbackMode", "playback_mode"), "ONCE"),
        }),
      });
      setNotice("Pengaturan suara pesanan berhasil disimpan.");
      await refreshAlertSettings();
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Pengaturan suara belum berhasil disimpan.",
      );
    } finally {
      setAlertBusyBranch(undefined);
    }
  };

  const uploadAlertAudio = async (
    setting: ApiRecord,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > 1_500_000) {
      setNotice("Suara maksimal 1,5 MB. Pilih potongan suara yang lebih pendek.");
      return;
    }
    const branchId = asText(get(setting, "branchId", "branch_id"));
    setAlertBusyBranch(branchId);
    try {
      const dataBase64 = await blobToBase64(file);
      await apiRequest(`/api/order-alerts/${encodeURIComponent(branchId)}/audio`, {
        method: "POST",
        ...jsonBody({ originalFilename: file.name, dataBase64 }),
      });
      setNotice("Suara pesanan cabang berhasil diunggah dan diaktifkan.");
      await refreshAlertSettings();
    } catch (caught) {
      setNotice(
        caught instanceof Error ? caught.message : "Suara belum berhasil diunggah.",
      );
    } finally {
      setAlertBusyBranch(undefined);
    }
  };

  const openOrderEditor = async (card: ApiRecord) => {
    const id = asText(card.id);
    setDetailBusyId(id);
    try {
      const payload = asRecord(
        await apiRequest<unknown>(`${endpoint}/${encodeURIComponent(id)}`),
      );
      const order = asRecord(payload.order);
      const orderItems = Array.isArray(payload.items)
        ? payload.items.map(asRecord)
        : [];
      const orderStatus = asText(get(order, "orderStatus", "order_status"));
      const paymentStatus = asText(
        get(order, "paymentStatus", "payment_status"),
      );
      if (
        !["CONFIRMED", "WAITING_PAYMENT", "PREPARING"].includes(orderStatus) ||
        ["PAID", "REFUNDED"].includes(paymentStatus)
      ) {
        setNotice(
          "Item hanya dapat diedit setelah dikonfirmasi, sebelum siap dikirim/diambil, dan sebelum pembayaran selesai.",
        );
        return;
      }
      setEditQuantities(
        Object.fromEntries(
          orderItems.map((line) => [
            asText(line.id),
            String(asNumber(line.quantity)),
          ]),
        ),
      );
      setEditReason("");
      setEditTarget({ card, order, items: orderItems });
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Data terbaru pesanan belum dapat dimuat.",
      );
    } finally {
      setDetailBusyId(undefined);
    }
  };

  const saveOrderEdit = async () => {
    if (!editTarget) return;
    const hasInvalidQuantity = editTarget.items.some((line) => {
      const orderItemId = asText(line.id);
      const quantity = Number(editQuantities[orderItemId]);
      return (
        !Number.isSafeInteger(quantity) ||
        quantity < 0 ||
        quantity > asNumber(line.quantity)
      );
    });
    if (hasInvalidQuantity) {
      setNotice("Jumlah baru harus angka utuh dari 0 sampai jumlah sebelumnya.");
      return;
    }
    const changes = editTarget.items.flatMap((line) => {
      const orderItemId = asText(line.id);
      const currentQuantity = asNumber(line.quantity);
      const quantity = Number(editQuantities[orderItemId]);
      return Number.isSafeInteger(quantity) && quantity < currentQuantity
        ? [{ orderItemId, quantity }]
        : [];
    });
    if (editReason.trim().length < 3) {
      setNotice("Tuliskan alasan perubahan item minimal 3 karakter.");
      return;
    }
    try {
      calculateOrderAmendment({
        subtotal: asNumber(editTarget.order.subtotal),
        deliveryFee: asNumber(
          get(editTarget.order, "deliveryFee", "delivery_fee"),
        ),
        pointsRedeemed: asNumber(
          get(editTarget.order, "pointsRedeemed", "points_redeemed"),
        ),
        pointValueRupiah: asNumber(
          get(
            editTarget.order,
            "pointValueSnapshot",
            "point_value_snapshot",
          ),
          MEMBER_POINT_VALUE_RUPIAH,
        ),
        lines: editTarget.items.map((line) => ({
          orderItemId: asText(line.id),
          quantity: asNumber(line.quantity),
          unitPrice: asNumber(get(line, "unitPrice", "unit_price")),
        })),
        changes,
      });
    } catch (caught) {
      const code = caught instanceof Error ? caught.message : "";
      setNotice(
        code === "ORDER_EDIT_LAST_ITEM"
          ? "Sisakan minimal satu item. Jika semua barang kosong, batalkan pesanannya."
          : "Kurangi minimal satu jumlah item. Jumlah tidak boleh ditambah dari menu ini.",
      );
      return;
    }

    const id = asText(editTarget.order.id, asText(editTarget.card.id));
    setEditBusy(true);
    try {
      const snapshot = asRecord(
        await apiRequest<unknown>(`${endpoint}/${encodeURIComponent(id)}/items`, {
          method: "PATCH",
          ...jsonBody({
            expectedVersion: asNumber(
              get(editTarget.order, "contentVersion", "content_version"),
              1,
            ),
            reason: editReason.trim(),
            items: changes,
          }),
        }),
      );
      setItems((current) =>
        current.map((row) =>
          asText(row.id) === id ? mergeAmendmentSnapshot(row, snapshot) : row,
        ),
      );
      setReceiptTarget((current) =>
        current && asText(current.id) === id
          ? mergeAmendmentSnapshot(current, snapshot)
          : current,
      );
      setDetails((current) => {
        const next = { ...current };
        delete next[id];
        return next;
      });
      setEditTarget(undefined);
      setNotice(
        `Isi pesanan berhasil diperbarui. ${asNumber(get(snapshot, "returnedStock", "returned_stock"))} stok dikembalikan ke cabang dan resi sudah berubah.`,
      );
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Isi pesanan belum berhasil diperbarui.",
      );
    } finally {
      setEditBusy(false);
    }
  };

  const openReceipt = async (item: ApiRecord) => {
    const id = asText(item.id);
    setDetailBusyId(id);
    try {
      const payload = asRecord(
        await apiRequest<unknown>(`${endpoint}/${encodeURIComponent(id)}`),
      );
      const order = asRecord(payload.order);
      const orderItems = Array.isArray(payload.items)
        ? payload.items.map(asRecord)
        : [];
      setReceiptPrintedAt(new Date().toISOString());
      setReceiptTarget({
        ...item,
        ...order,
        itemPreviewJson: JSON.stringify(orderItems),
        item_preview_json: JSON.stringify(orderItems),
        itemCount: orderItems.length,
        item_count: orderItems.length,
      });
    } catch (caught) {
      setNotice(
        caught instanceof Error
          ? caught.message
          : "Data terbaru untuk resi belum dapat dimuat.",
      );
    } finally {
      setDetailBusyId(undefined);
    }
  };

  const receiptItems = receiptTarget
    ? parseOrderItemPreview(
        get(
          receiptTarget,
          "itemPreviewJson",
          "item_preview_json",
          "itemPreview",
        ),
      )
    : [];
  const receiptTimeZone = receiptTarget
    ? orderTimeZone(receiptTarget)
    : "Asia/Makassar";
  const visibleItems = unmarkedPayment ? items.filter(needsPaymentMark) : items;
  const paginationPages = Array.from(
    new Set([
      1,
      Math.max(1, ordersPage - 1),
      ordersPage,
      ...(ordersHasMore ? [ordersPage + 1] : []),
    ]),
  ).sort((left, right) => left - right);
  const editorChanges = editTarget
    ? editTarget.items.flatMap((line) => {
        const orderItemId = asText(line.id);
        const currentQuantity = asNumber(line.quantity);
        const quantity = Number(editQuantities[orderItemId]);
        return Number.isSafeInteger(quantity) && quantity < currentQuantity
          ? [{ orderItemId, quantity }]
          : [];
      })
    : [];
  let editorEstimate: ReturnType<typeof calculateOrderAmendment> | undefined;
  if (editTarget && editorChanges.length > 0) {
    try {
      editorEstimate = calculateOrderAmendment({
        subtotal: asNumber(editTarget.order.subtotal),
        deliveryFee: asNumber(
          get(editTarget.order, "deliveryFee", "delivery_fee"),
        ),
        pointsRedeemed: asNumber(
          get(editTarget.order, "pointsRedeemed", "points_redeemed"),
        ),
        pointValueRupiah: asNumber(
          get(
            editTarget.order,
            "pointValueSnapshot",
            "point_value_snapshot",
          ),
          MEMBER_POINT_VALUE_RUPIAH,
        ),
        lines: editTarget.items.map((line) => ({
          orderItemId: asText(line.id),
          quantity: asNumber(line.quantity),
          unitPrice: asNumber(get(line, "unitPrice", "unit_price")),
        })),
        changes: editorChanges,
      });
    } catch {
      editorEstimate = undefined;
    }
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-heading">
        <div>
          <span className="eyebrow">Operasional cabang</span>
          <h1>{paymentsOnly ? "Pembayaran" : "Pesanan"}</h1>
          <p>
            {paymentsOnly
              ? "Catat hasil pemeriksaan pembayaran manual. Menu ini tidak menarik uang atau memproses transfer otomatis."
              : "Lihat isi belanja, rincian harga, dan lama setiap tahap dalam satu kartu pesanan."}
          </p>
        </div>
        <Button variant="secondary" onClick={() => void load()} icon={<RefreshCw />}>
          Perbarui
        </Button>
      </div>

      {!paymentsOnly && (
        <Notice tone="info">
          <strong>Urutan prioritas:</strong> pesanan baru yang paling lama belum
          dikonfirmasi berada paling atas, lalu pesanan yang sedang diproses,
          siap diambil/dikirim, dan sedang dikirim. Pesanan selesai, dibatalkan,
          atau ditolak otomatis turun ke bawah.
        </Notice>
      )}

      {paymentsOnly && (
        <Notice tone="info">
          Gunakan menu ini untuk pesanan transfer, QRIS manual, COD, atau bayar
          di toko. Pilih status di bawah untuk melihat pembayaran yang belum
          dibayar, menunggu verifikasi, sudah dibayar, atau ditolak.
        </Notice>
      )}

      {!paymentsOnly && role === "OWNER" && (
        <section className="dashboard-panel order-alert-owner-panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Alarm per cabang</span>
              <h2>Suara pesanan baru</h2>
              <p>
                Upload MP3, WAV, atau OGG maksimal 1,5 MB. Setiap cabang dapat
                memakai suara dan cara putar yang berbeda. Alarm tetap aktif
                di semua halaman Admin/Owner. Suara berulang berhenti otomatis
                setelah semua pesanan cabang dikonfirmasi atau suara dimatikan.
              </p>
            </div>
          </div>
          <div className="order-alert-setting-grid">
            {alertSettings.map((setting) => {
              const branchId = asText(get(setting, "branchId", "branch_id"));
              const enabled = asBoolean(get(setting, "isEnabled", "is_enabled"));
              return (
                <article key={branchId}>
                  <div>
                    <Music2 aria-hidden="true" />
                    <span>
                      <strong>{asText(get(setting, "branchName", "branch_name"))}</strong>
                      <small>
                        {asText(
                          get(setting, "originalFilename", "original_filename"),
                          "Belum ada suara",
                        )}
                      </small>
                    </span>
                  </div>
                  <label>
                    Cara putar
                    <select
                      value={asText(
                        get(setting, "playbackMode", "playback_mode"),
                        "ONCE",
                      )}
                      disabled={alertBusyBranch === branchId}
                      onChange={(event) =>
                        void saveAlertSetting(setting, {
                          playbackMode: event.target.value,
                        })
                      }
                    >
                      <option value="ONCE">Putar 1 kali</option>
                      <option value="REPEAT_UNTIL_CONFIRMED">
                        Ulangi sampai semua dikonfirmasi
                      </option>
                    </select>
                  </label>
                  <div className="order-alert-setting-actions">
                    <label className="button button--secondary order-alert-upload">
                      <Upload aria-hidden="true" />
                      {alertBusyBranch === branchId ? "Mengunggah…" : "Upload suara"}
                      <input
                        type="file"
                        accept="audio/mpeg,audio/wav,audio/ogg,.mp3,.wav,.ogg"
                        disabled={alertBusyBranch === branchId}
                        onChange={(event) => void uploadAlertAudio(setting, event)}
                      />
                    </label>
                    <Button
                      variant={enabled ? "danger" : "secondary"}
                      disabled={alertBusyBranch === branchId}
                      onClick={() =>
                        void saveAlertSetting(setting, { isEnabled: !enabled })
                      }
                    >
                      {enabled ? "Nonaktifkan" : "Aktifkan"}
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {!paymentsOnly && (
        <div className="order-sound-toolbar">
          <div className={`realtime-status ${isLive ? "realtime-status--connected" : "realtime-status--offline"}`}>
            <span aria-hidden="true" />
            {isLive
              ? "LIVE · internet & notifikasi aktif di semua menu"
              : "Menyambungkan notifikasi real-time"}
          </div>
          <button
            type="button"
            className="sound-toggle"
            onClick={() =>
              soundBlocked || muted ? activateSound() : setMuted(true)
            }
            aria-label={
              soundBlocked
                ? "Izinkan suara pesanan"
                : muted
                  ? "Aktifkan suara pesanan"
                  : "Matikan suara pesanan"
            }
          >
            {soundBlocked || muted ? <VolumeX /> : <Volume2 />}
            {soundBlocked ? "Izinkan suara" : muted ? "Suara mati" : "Suara aktif"}
          </button>
          <label className="volume-control">
            <span>Volume {Math.round(volume * 100)}%</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={volume}
              onChange={(event) => setVolume(Number(event.target.value))}
            />
          </label>
        </div>
      )}
      {soundBlocked && !muted && (
        <Notice tone="warning">
          Browser menahan suara otomatis. Tekan <strong>Suara aktif</strong>
          satu kali untuk mengizinkan alarm di perangkat ini.
        </Notice>
      )}
      {!paymentsOnly && (
        <Notice tone="info">
          Reply pesan WhatsApp terkait dengan <strong>SELESAI</strong> setelah
          barang benar-benar diserahkan. Admin hanya dapat menghapus pesanan
          terminal; Owner menyimpan tampilan arsip 30 hari.
        </Notice>
      )}
      {notice && (
        <Notice
          tone={
            notice.includes("berhasil") || notice.includes("30 hari")
              ? "success"
              : notice.includes("Pesanan baru")
                ? "warning"
                : "danger"
          }
        >
          {notice}
        </Notice>
      )}

      <div className="toolbar order-filter-toolbar">
        <label className="dashboard-search">
          <Search />
          <input
            value={query}
            onChange={(event) => { setQuery(event.target.value); setOrdersPage(1); }}
            placeholder="Nomor pesanan atau pelanggan…"
          />
        </label>
        <label className="toolbar-filter">
          <Filter />
          <select aria-label={paymentsOnly ? "Filter status pembayaran" : "Filter status pesanan"} value={status} onChange={(event) => { setStatus(event.target.value); setOrdersPage(1); }}>
            <option value="">{paymentsOnly ? "Semua pembayaran" : "Semua status"}</option>
            {paymentsOnly ? (
              <>
                <option value="UNPAID">Belum dibayar</option>
                <option value="WAITING_VERIFICATION">Menunggu verifikasi</option>
                <option value="PAID">Sudah dibayar</option>
                <option value="REJECTED">Pembayaran ditolak</option>
                <option value="REFUNDED">Dikembalikan</option>
              </>
            ) : (
              <>
                <option value={UNMARKED_PAYMENT_FILTER}>
                  Tandai sudah bayar ({unpaidCount ?? "…"})
                </option>
                <option value="NEW">Pesanan baru</option>
                <option value="CONFIRMED">Dikonfirmasi</option>
                <option value="PREPARING">Disiapkan</option>
                <option value="READY_FOR_DELIVERY">Siap dikirim</option>
                <option value="DELIVERING">Sedang dikirim</option>
                <option value="READY_FOR_PICKUP">Siap diambil</option>
                <option value="COMPLETED">Selesai</option>
                <option value="CANCELLED">Dibatalkan</option>
              </>
            )}
          </select>
        </label>
        <label className="date-filter">
          <CalendarDays aria-hidden="true" />
          <span>Dari tanggal</span>
          <input
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(event) => { setDateFrom(event.target.value); setOrdersPage(1); }}
          />
        </label>
        <label className="date-filter">
          <CalendarDays aria-hidden="true" />
          <span>Sampai tanggal</span>
          <input
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(event) => { setDateTo(event.target.value); setOrdersPage(1); }}
          />
        </label>
        <fieldset className="order-card-size" aria-label="Ukuran kartu pesanan">
          <legend>Ukuran kartu</legend>
          {([
            ["compact", "Ringkas"],
            ["comfortable", "Sedang"],
            ["large", "Besar"],
          ] as const).map(([value, label]) => (
            <button
              type="button"
              key={value}
              className={cardSize === value ? "is-active" : ""}
              aria-pressed={cardSize === value}
              onClick={() => setCardSize(value)}
            >
              {label}
            </button>
          ))}
        </fieldset>
      </div>

      {unmarkedPayment && (
        <Notice tone="info">
          Pesanan yang belum ditandai dibayar, termasuk yang masih diproses.
          Pesanan dibatalkan/ditolak dan dana dikembalikan tidak dihitung.
          Jumlah mengikuti pencarian dan tanggal yang dipilih.
        </Notice>
      )}
      {loading && <Skeleton rows={6} />}
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!loading && !error && visibleItems.length === 0 && (
        <EmptyState
          title={
            paymentsOnly
              ? "Belum ada pembayaran untuk filter ini"
              : "Belum ada pesanan untuk filter ini"
          }
          description={
            paymentsOnly
              ? "Ubah filter ke Semua pembayaran untuk melihat seluruh catatan."
              : undefined
          }
        />
      )}
      {!loading && !error && (
        <div className={`order-management-list order-management-list--${cardSize}`}>
          {visibleItems.map((item) => {
            const id = asText(item.id);
            const orderStatus = asText(get(item, "orderStatus", "order_status"));
            const paymentStatus = asText(
              get(item, "paymentStatus", "payment_status"),
            );
            const serviceTimeZone = orderTimeZone(item);
            const deliveryType = asText(
              get(item, "deliveryType", "delivery_type"),
              "PICKUP",
            );
            const next = nextOrderStatus(orderStatus, deliveryType);
            const archivedUntil = asText(
              get(item, "adminHiddenUntil", "admin_hidden_until"),
            );
            const detail = details[id];
            const completedAndPaid =
              orderStatus === "COMPLETED" && paymentStatus === "PAID";
            const canEditItems =
              ["CONFIRMED", "WAITING_PAYMENT", "PREPARING"].includes(
                orderStatus,
              ) && !["PAID", "REFUNDED"].includes(paymentStatus);
            const orderIdentity = asText(
              get(item, "orderNumber", "order_number"),
              id,
            );
            const itemPreview = parseOrderItemPreview(
              get(item, "itemPreviewJson", "item_preview_json", "itemPreview"),
            );
            return (
              <article
                className={`management-card management-card--${orderStatus.toLowerCase()} order-card-color-${orderColorIndex(orderIdentity)}`}
                key={id}
              >
                <div className="management-card__top">
                  <div>
                    <span className="order-branch-label">
                      {asText(
                        get(
                          item,
                          "branchName",
                          "branch_name",
                          "branch_name_snapshot",
                        ),
                        "Cabang",
                      )}
                    </span>
                    <strong>{asText(get(item, "orderNumber", "order_number"))}</strong>
                  </div>
                  <StatusBadge status={paymentsOnly ? paymentStatus : orderStatus} />
                </div>
                {completedAndPaid && (
                  <div className="order-completion-summary">
                    <CheckCircle2 aria-hidden="true" />
                    <strong>Selesai dan sudah dibayar</strong>
                  </div>
                )}
                {archivedUntil && (
                  <Notice tone="warning">
                    Diarsipkan Admin {asText(get(item, "adminHiddenBranches", "admin_hidden_branches"))}.
                    Tersedia untuk Owner sampai {formatDateTime(archivedUntil, serviceTimeZone)}.
                  </Notice>
                )}
                <div className="management-card__facts management-card__facts--order">
                  <span>
                    <small>Pelanggan</small>
                    <strong>{asText(get(item, "customerName", "customer_name", "customer_name_snapshot"))}</strong>
                  </span>
                  <span>
                    <small>WhatsApp</small>
                    <strong>{asText(get(item, "whatsappNumber", "whatsapp_number", "customer_whatsapp_snapshot"))}</strong>
                  </span>
                  <span>
                    <small>Pengiriman</small>
                    <strong>{deliveryType === "DELIVERY" ? "Antar" : "Ambil"}</strong>
                  </span>
                  <span>
                    <small>Mulai tahap ini</small>
                    <strong>
                      {formatOrderStageDuration(
                        asText(
                          get(
                            item,
                            "currentStatusStartedAt",
                            "current_status_started_at",
                            "createdAt",
                            "created_at",
                          ),
                        ),
                      )}
                    </strong>
                  </span>
                  <span>
                    <small>Pesanan masuk</small>
                    <strong>{formatDateTime(asText(get(item, "createdAt", "created_at")), serviceTimeZone)}</strong>
                  </span>
                </div>

                <section className="order-lines-section" aria-labelledby={`order-items-${id}`}>
                  <div className="order-lines-heading">
                    <h3 id={`order-items-${id}`}>Rincian Pesanan</h3>
                    <span>
                      {asNumber(get(item, "itemCount", "item_count"))} item
                    </span>
                  </div>
                  {itemPreview.length > 0 ? (
                    <div className="order-items-table-wrap">
                      <table className="order-items-table">
                        <thead>
                          <tr>
                            <th scope="col">Produk</th>
                            <th scope="col">Jumlah</th>
                            <th scope="col">Harga Satuan</th>
                            <th scope="col">Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {itemPreview.map((line, index) => (
                            <tr key={`${line.productName}-${index}`}>
                              <td data-label="Produk">
                                <strong>{line.productName}</strong>
                              </td>
                              <td data-label="Jumlah">
                                <span className="order-item-amount">
                                  <strong>{line.quantity}</strong>
                                  <i aria-hidden="true">•</i>
                                  <span>{line.unit || "unit"}</span>
                                </span>
                              </td>
                              <td data-label="Harga Satuan">
                                {formatRupiah(line.unitPrice)}
                              </td>
                              <td data-label="Subtotal">
                                <strong>{formatRupiah(line.subtotal)}</strong>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p>
                      {asText(
                        get(item, "itemSummary", "item_summary"),
                        "Rincian item belum tersedia.",
                      )}
                    </p>
                  )}
                  <dl className="order-totals">
                    <div>
                      <dt>Subtotal Barang</dt>
                      <dd>{formatRupiah(asNumber(item.subtotal))}</dd>
                    </div>
                    <div>
                      <dt>Ongkir</dt>
                      <dd>{formatRupiah(asNumber(get(item, "deliveryFee", "delivery_fee")))}</dd>
                    </div>
                    <div>
                      <dt>Potongan</dt>
                      <dd>
                        {asNumber(item.discount) > 0 ? "-" : ""}
                        {formatRupiah(asNumber(item.discount))}
                      </dd>
                    </div>
                    <div className="order-totals__grand">
                      <dt>TOTAL</dt>
                      <dd>{formatRupiah(asNumber(item.total))}</dd>
                    </div>
                  </dl>
                </section>

                <section className="order-information" aria-label="Informasi pemesanan">
                  <h3>Informasi Pemesanan</h3>
                  <dl>
                    <div>
                      <dt>Metode pembayaran</dt>
                      <dd>{asText(get(item, "paymentMethodNameSnapshot", "payment_method_name_snapshot"), "Manual")}</dd>
                    </div>
                    <div>
                      <dt>Alamat antar</dt>
                      <dd>{asText(get(item, "deliveryAddress", "delivery_address"), "Diambil di cabang")}</dd>
                    </div>
                    <div>
                      <dt>Catatan pelanggan</dt>
                      <dd>{asText(get(item, "customerNote", "customer_note"), "Tidak ada catatan")}</dd>
                    </div>
                  </dl>
                </section>

                {/* Tampilan lama disimpan sementara sebagai referensi migrasi UI.
                <section className="order-purchase-summary">
                  <div className="order-purchase-summary__items">
                    <div className="order-purchase-summary__heading">
                      <h3>Barang yang dibeli</h3>
                      <span>
                        Isi pesanan ({asNumber(get(item, "itemCount", "item_count"))} item)
                      </span>
                    </div>
                    {itemPreview.length > 0 ? (
                      <div className="order-item-preview-list">
                        {itemPreview.map((line, index) => (
                          <div key={`${line.productName}-${index}`}>
                            <span>{line.productName}</span>
                            <small className="order-item-amount">
                              <strong>{line.quantity}</strong>
                              <i aria-hidden="true">•</i>
                              <span>{line.unit || "unit"}</span>
                            </small>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>
                        {asText(
                          get(item, "itemSummary", "item_summary"),
                          "Rincian item belum tersedia.",
                        )}
                      </p>
                    )}
                  </div>
                  <dl className="order-price-stack">
                    <div>
                      <dt>Belanja</dt>
                      <dd>{formatRupiah(asNumber(item.subtotal))}</dd>
                    </div>
                    <div>
                      <dt>Ongkir</dt>
                      <dd>{formatRupiah(asNumber(get(item, "deliveryFee", "delivery_fee")))}</dd>
                    </div>
                    <div>
                      <dt>Potongan</dt>
                      <dd>-{formatRupiah(asNumber(item.discount))}</dd>
                    </div>
                    <div className="order-price-stack__total">
                      <dt>Total</dt>
                      <dd>{formatRupiah(asNumber(item.total))}</dd>
                    </div>
                  </dl>
                </section>
                */}

                <Button
                  variant="quiet"
                  busy={detailBusyId === id}
                  onClick={() =>
                    expandedId === id
                      ? setExpandedId(undefined)
                      : void fetchDetail(id)
                  }
                  icon={expandedId === id ? <ChevronUp /> : <ChevronDown />}
                >
                  {expandedId === id ? "Tutup waktu tahap" : "Lihat lama setiap tahap"}
                </Button>

                {expandedId === id && detail && (
                  <div className="order-detail-panel">
                    {/* Item dan informasi pemesanan sudah tampil satu kali di atas.
                    <div className="order-detail-products">
                      <h3>Harga per barang</h3>
                      {detail.items.map((line) => (
                        <div key={asText(line.id)}>
                          <span>
                            <strong>{asText(get(line, "productNameSnapshot", "product_name_snapshot"))}</strong>
                            <small className="order-detail-product-meta">
                              <span className="order-item-amount">
                                <strong>{asNumber(line.quantity)}</strong>
                                <i aria-hidden="true">•</i>
                                <span>{asText(get(line, "productUnitSnapshot", "product_unit_snapshot"), "unit")}</span>
                              </span>
                              <span>{formatRupiah(asNumber(get(line, "unitPrice", "unit_price")))} per {asText(get(line, "productUnitSnapshot", "product_unit_snapshot"), "unit")}</span>
                            </small>
                          </span>
                          <b>{formatRupiah(asNumber(line.subtotal))}</b>
                        </div>
                      ))}
                    </div>
                    <dl className="order-detail-facts">
                      <div>
                        <dt>Metode pembayaran</dt>
                        <dd>{asText(get(detail.order, "paymentMethodNameSnapshot", "payment_method_name_snapshot"), "Manual")}</dd>
                      </div>
                      <div>
                        <dt>Alamat antar</dt>
                        <dd>{asText(get(detail.order, "deliveryAddress", "delivery_address"), "Diambil di cabang")}</dd>
                      </div>
                      <div>
                        <dt>Catatan pelanggan</dt>
                        <dd>{asText(get(detail.order, "customerNote", "customer_note"), "Tidak ada catatan")}</dd>
                      </div>
                    </dl>
                    */}
                    {detail.amendments.length > 0 && (
                      <section className="order-amendment-history">
                        <h3>Riwayat perubahan item</h3>
                        {detail.amendments.map((amendment) => (
                          <div key={asText(amendment.id)}>
                            <span>
                              <strong>
                                {asText(
                                  get(
                                    amendment,
                                    "requestedByName",
                                    "requested_by_name",
                                  ),
                                  asText(
                                    get(amendment, "requestedVia", "requested_via"),
                                    "Staf",
                                  ),
                                )}
                              </strong>
                              <small>
                                {formatDateTime(
                                  asText(get(amendment, "createdAt", "created_at")),
                                  serviceTimeZone,
                                )}
                              </small>
                            </span>
                            <span>
                              <b>{asText(amendment.reason)}</b>
                              <small>
                                {formatRupiah(
                                  asNumber(get(amendment, "oldTotal", "old_total")),
                                )}{" "}
                                → {formatRupiah(
                                  asNumber(get(amendment, "newTotal", "new_total")),
                                )}
                              </small>
                            </span>
                          </div>
                        ))}
                      </section>
                    )}
                    <div className="order-stage-timeline">
                      <h3>Lama setiap tahap</h3>
                      {detail.events.map((event, index) => {
                        const nextEvent = detail.events[index + 1];
                        const eventStatus = asText(get(event, "toStatus", "to_status"));
                        const endedAt = nextEvent
                          ? asText(get(nextEvent, "createdAt", "created_at"))
                          : terminalStatuses.has(eventStatus)
                            ? asText(get(detail.order, "updatedAt", "updated_at"))
                            : undefined;
                        return (
                          <div key={asText(event.id)}>
                            <span className="timeline-dot" aria-hidden="true" />
                            <span>
                              <strong>{statusLabel(eventStatus)}</strong>
                              <small>{formatDateTimeWithZone(asText(get(event, "createdAt", "created_at")), serviceTimeZone)}</small>
                            </span>
                            <b>
                              <Clock3 aria-hidden="true" />
                              {terminalStatuses.has(eventStatus) && !nextEvent
                                ? "Tahap akhir"
                                : formatOrderStageDuration(
                                    asText(get(event, "createdAt", "created_at")),
                                    endedAt,
                                  )}
                            </b>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="management-card__actions">
                  {paymentsOnly ? (
                    <>
                      {["UNPAID", "WAITING_VERIFICATION"].includes(paymentStatus) && (
                        <Button disabled={Boolean(busyId)} busy={busyId === id} onClick={() => void update(item, "PAID", true)}>
                          Tandai sudah dibayar
                        </Button>
                      )}
                      {paymentStatus === "WAITING_VERIFICATION" && (
                        <Button disabled={Boolean(busyId)} busy={busyId === id} variant="danger" onClick={() => void update(item, "REJECTED", true)}>
                          Tolak pembayaran
                        </Button>
                      )}
                      {paymentStatus === "PAID" && (
                        <Notice tone="success">Pembayaran sudah dicatat lunas.</Notice>
                      )}
                      {paymentStatus === "REJECTED" && (
                        <Notice tone="warning">Pembayaran ini sudah ditolak.</Notice>
                      )}
                    </>
                  ) : (
                    <>
                      {!unconfirmedStatuses.has(orderStatus) &&
                        !["CANCELLED", "REJECTED"].includes(orderStatus) && (
                          <Button
                            variant="secondary"
                            disabled={Boolean(busyId)}
                            busy={detailBusyId === id}
                            onClick={() => void openReceipt(item)}
                            icon={<Printer aria-hidden="true" />}
                          >
                            Print Resi
                          </Button>
                        )}
                      {canEditItems && (
                        <Button
                          variant="secondary"
                          disabled={Boolean(busyId)}
                          busy={detailBusyId === id}
                          onClick={() => void openOrderEditor(item)}
                          icon={<PencilLine aria-hidden="true" />}
                        >
                          Edit Item
                        </Button>
                      )}
                      {next && (
                        <Button disabled={Boolean(busyId)} busy={busyId === id} onClick={() => void update(item, next)}>
                          Lanjut: {statusLabel(next)}
                        </Button>
                      )}
                      {orderStatus === "DELIVERING" && (
                        <Button disabled={Boolean(busyId)} busy={busyId === id} variant="secondary" onClick={() => void update(item, "COMPLETED")}>
                          Selesai Manual
                        </Button>
                      )}
                      {orderStatus === "COMPLETED" && paymentStatus !== "PAID" && (
                        <Button
                          busy={busyId === id}
                          disabled={Boolean(busyId)}
                          onClick={() => void update(item, "PAID", true)}
                          icon={<CheckCircle2 aria-hidden="true" />}
                        >
                          Tandai sudah dibayar
                        </Button>
                      )}
                      {!terminalStatuses.has(orderStatus) && (
                        <Button disabled={Boolean(busyId)} busy={busyId === id} variant="danger" onClick={() => void update(item, "CANCELLED")}>
                          Batalkan
                        </Button>
                      )}
                      {role === "ADMIN" && terminalStatuses.has(orderStatus) && (
                        <Button disabled={Boolean(busyId)} variant="danger" onClick={() => setArchiveTarget(item)} icon={<Trash2 />}>
                          Hapus dari Admin
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {!error && (ordersPage > 1 || ordersHasMore) && (
        <nav className="catalog-pagination" aria-label="Halaman pesanan">
          <button
            type="button"
            disabled={loading || Boolean(busyId) || ordersPage === 1}
            onClick={() => setOrdersPage((page) => Math.max(1, page - 1))}
          >
            Sebelumnya
          </button>
          {paginationPages.map((page, index) => (
            <span key={page} className="catalog-pagination__page-slot">
              {index > 0 && page - (paginationPages[index - 1] ?? page) > 1 && (
                <span aria-hidden="true">…</span>
              )}
              <button
                type="button"
                className={page === ordersPage ? "active" : ""}
                aria-current={page === ordersPage ? "page" : undefined}
                disabled={loading || Boolean(busyId) || page === ordersPage}
                onClick={() => setOrdersPage(page)}
              >
                {page}
              </button>
            </span>
          ))}
          <span className="catalog-pagination__summary" aria-live="polite">
            <strong>Halaman {ordersPage}</strong>
            <small>Maksimal {ORDERS_PAGE_SIZE} pesanan per halaman</small>
          </span>
          <button
            type="button"
            disabled={loading || Boolean(busyId) || !ordersHasMore}
            onClick={() => setOrdersPage((page) => page + 1)}
          >
            Berikutnya
          </button>
        </nav>
      )}
      <Modal
        open={Boolean(editTarget)}
        onClose={() => {
          if (!editBusy) setEditTarget(undefined);
        }}
        title={`Edit Item Pesanan #${asText(
          get(editTarget?.order ?? {}, "orderNumber", "order_number"),
        )}`}
        description="Kurangi jumlah atau hapus barang yang ternyata tidak tersedia. Stok dan total dikoreksi dalam satu transaksi."
      >
        {editTarget && (
          <div className="order-item-editor">
            <Notice tone="warning">
              Menu ini hanya untuk <strong>mengurangi</strong> barang setelah
              konfirmasi. Selisih stok otomatis kembali ke cabang yang membuat
              pesanan; data pelanggan dan nomor pesanan tidak berubah.
            </Notice>
            <div className="order-item-editor__list">
              {editTarget.items.map((line) => {
                const itemId = asText(line.id);
                const currentQuantity = asNumber(line.quantity);
                return (
                  <article key={itemId}>
                    <div>
                      <strong>
                        {asText(
                          get(
                            line,
                            "productNameSnapshot",
                            "product_name_snapshot",
                          ),
                        )}
                      </strong>
                      <small>
                        Sebelumnya {currentQuantity} • {asText(
                          get(
                            line,
                            "productUnitSnapshot",
                            "product_unit_snapshot",
                          ),
                          "unit",
                        )}{" "}
                        · {formatRupiah(
                          asNumber(get(line, "unitPrice", "unit_price")),
                        )}
                      </small>
                    </div>
                    <label>
                      Jumlah baru
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        max={currentQuantity}
                        step="1"
                        value={editQuantities[itemId] ?? String(currentQuantity)}
                        disabled={editBusy}
                        onChange={(event) =>
                          setEditQuantities((current) => ({
                            ...current,
                            [itemId]: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <Button
                      variant="danger"
                      disabled={editBusy || editQuantities[itemId] === "0"}
                      onClick={() =>
                        setEditQuantities((current) => ({
                          ...current,
                          [itemId]: "0",
                        }))
                      }
                    >
                      Hapus Item
                    </Button>
                  </article>
                );
              })}
            </div>
            <label className="order-item-editor__reason">
              Alasan perubahan
              <textarea
                rows={2}
                maxLength={300}
                value={editReason}
                disabled={editBusy}
                onChange={(event) => setEditReason(event.target.value)}
                placeholder="Contoh: Barang fisik habis saat pesanan disiapkan"
              />
            </label>
            {editorEstimate ? (
              <dl className="order-item-editor__summary">
                <div>
                  <dt>Stok dikembalikan</dt>
                  <dd>{editorEstimate.returnedStock}</dd>
                </div>
                <div>
                  <dt>Subtotal baru</dt>
                  <dd>{formatRupiah(editorEstimate.newSubtotal)}</dd>
                </div>
                <div>
                  <dt>Potongan baru</dt>
                  <dd>-{formatRupiah(editorEstimate.newDiscount)}</dd>
                </div>
                <div className="order-item-editor__total">
                  <dt>Total baru</dt>
                  <dd>{formatRupiah(editorEstimate.newTotal)}</dd>
                </div>
              </dl>
            ) : (
              <Notice tone="info">
                Ubah minimal satu jumlah dan sisakan minimal satu item dalam
                pesanan.
              </Notice>
            )}
            <div className="modal-actions">
              <Button
                variant="secondary"
                disabled={editBusy}
                onClick={() => setEditTarget(undefined)}
              >
                Batal
              </Button>
              <Button
                busy={editBusy}
                disabled={!editorEstimate || editReason.trim().length < 3}
                onClick={() => void saveOrderEdit()}
              >
                Simpan & Perbarui Resi
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(archiveTarget)}
        onClose={() => setArchiveTarget(undefined)}
        title="Hapus pesanan dari Admin?"
        description="Pesanan hilang dari daftar Admin. Owner masih dapat melihat arsip ini selama 30 hari; data audit dan laporan tidak dirusak."
      >
        <div className="modal-actions">
          <Button variant="secondary" onClick={() => setArchiveTarget(undefined)}>
            Batal
          </Button>
          <Button variant="danger" busy={Boolean(busyId)} onClick={() => void archive()}>
            Hapus & Arsipkan
          </Button>
        </div>
      </Modal>

      <Modal
        open={Boolean(receiptTarget)}
        onClose={() => setReceiptTarget(undefined)}
        title="Preview Resi Pesanan"
        description="Periksa tampilannya terlebih dahulu. Hanya bagian resi di bawah yang akan dicetak."
      >
        {receiptTarget && (
          <>
            <article className="receipt-preview print-receipt">
              <header className="receipt-preview__header">
                <span>Warung Sedap Sayur</span>
                <h2>RESI PESANAN</h2>
                <strong>
                  #{asText(get(receiptTarget, "orderNumber", "order_number"))}
                </strong>
                <p>
                  {asText(
                    get(
                      receiptTarget,
                      "branchName",
                      "branch_name",
                      "branch_name_snapshot",
                    ),
                    "Cabang",
                  )}
                </p>
              </header>

              <dl className="receipt-preview__facts">
                <div>
                  <dt>Nama</dt>
                  <dd>
                    {asText(
                      get(
                        receiptTarget,
                        "customerName",
                        "customer_name",
                        "customer_name_snapshot",
                      ),
                    )}
                  </dd>
                </div>
                <div>
                  <dt>WhatsApp</dt>
                  <dd>
                    {asText(
                      get(
                        receiptTarget,
                        "whatsappNumber",
                        "whatsapp_number",
                        "customer_whatsapp_snapshot",
                      ),
                    )}
                  </dd>
                </div>
                <div>
                  <dt>Dicetak</dt>
                  <dd>{formatDateTime(receiptPrintedAt, receiptTimeZone)}</dd>
                </div>
              </dl>

              <section className="receipt-preview__items" aria-label="Rincian barang">
                {receiptItems.map((line, index) => (
                  <div key={`${line.productName}-${index}`}>
                    <span>
                      <strong>{line.productName}</strong>
                      <small>
                        {line.quantity} × {line.unit || "unit"} · {formatRupiah(line.unitPrice)}
                      </small>
                    </span>
                    <b>{formatRupiah(line.subtotal)}</b>
                  </div>
                ))}
              </section>

              <section className="receipt-preview__order-info" aria-label="Informasi pemesanan">
                <h3>Informasi Pemesanan</h3>
                <dl>
                  <div>
                    <dt>Metode pembayaran</dt>
                    <dd>
                      {asText(
                        get(
                          receiptTarget,
                          "paymentMethodNameSnapshot",
                          "payment_method_name_snapshot",
                        ),
                        "Manual",
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>{asText(get(receiptTarget, "deliveryType", "delivery_type")) === "DELIVERY" ? "Patokan untuk ongkir" : "Diambil di cabang"}</dt>
                    <dd>
                      {asText(get(receiptTarget, "deliveryType", "delivery_type")) === "DELIVERY"
                        ? asText(get(receiptTarget, "deliveryAddress", "delivery_address"), "Alamat belum diisi")
                        : asText(
                            get(
                              receiptTarget,
                              "branchName",
                              "branch_name",
                              "branch_name_snapshot",
                            ),
                            "Cabang",
                          )}
                    </dd>
                  </div>
                  {asText(get(receiptTarget, "deliveryType", "delivery_type")) === "DELIVERY" && (
                    <div>
                      <dt>Detail alamat lengkap dengan nomor rumah</dt>
                      <dd>
                        {asText(
                          get(receiptTarget, "deliveryLandmark", "delivery_landmark"),
                          "Detail alamat belum diisi",
                        )}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt>Catatan pelanggan</dt>
                    <dd>
                      {asText(
                        get(receiptTarget, "customerNote", "customer_note"),
                        "Tidak ada catatan",
                      )}
                    </dd>
                  </div>
                </dl>
              </section>

              <dl className="receipt-preview__totals">
                <div>
                  <dt>Subtotal</dt>
                  <dd>{formatRupiah(asNumber(receiptTarget.subtotal))}</dd>
                </div>
                {asNumber(get(receiptTarget, "deliveryFee", "delivery_fee")) >
                  0 && (
                  <div>
                    <dt>Ongkir</dt>
                    <dd>
                      {formatRupiah(
                        asNumber(
                          get(receiptTarget, "deliveryFee", "delivery_fee"),
                        ),
                      )}
                    </dd>
                  </div>
                )}
                {asNumber(receiptTarget.discount) > 0 && (
                  <div>
                    <dt>Potongan</dt>
                    <dd>-{formatRupiah(asNumber(receiptTarget.discount))}</dd>
                  </div>
                )}
                <div className="receipt-preview__grand-total">
                  <dt>TOTAL</dt>
                  <dd>{formatRupiah(asNumber(receiptTarget.total))}</dd>
                </div>
              </dl>
              <footer>Terima kasih sudah berbelanja.</footer>
            </article>
            <div className="modal-actions receipt-preview__actions">
              <Button
                variant="secondary"
                onClick={() => setReceiptTarget(undefined)}
              >
                Tutup
              </Button>
              <Button
                onClick={() => window.print()}
                icon={<Printer aria-hidden="true" />}
              >
                Print Resi
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
